import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { defaultLocale, LOCALES, type Locale } from "@tc/i18n";
import { LANG_STORAGE_KEY } from "./config";

/**
 * Active UI language (EN/ES) for the app (TW-3434).
 *
 * This is the single source of truth that wraps react-i18next: `lang` mirrors
 * `i18n.language` and `setLang` drives `i18n.changeLanguage`, persisting the choice to
 * localStorage (the anonymous/pre-login fallback) and — when an authenticated context
 * supplies `persist` — to `user.language` on the server, which is the durable default the
 * next session and every email/notification read from.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "/api";

function isLocale(value: string | null | undefined): value is Locale {
  return value != null && (LOCALES as readonly string[]).includes(value);
}

/** Best-effort write of the language preference to `user.language` (PATCH /api/me/language). */
export async function persistLanguage(lang: Locale): Promise<void> {
  try {
    await fetch(`${API_URL}/me/language`, {
      method: "PATCH",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ language: lang }),
    });
  } catch {
    // Non-fatal: localStorage already holds the choice for this device.
  }
}

interface LanguageValue {
  lang: Locale;
  setLang: (lang: Locale) => void;
}

const LanguageContext = createContext<LanguageValue | null>(null);

export function LanguageProvider({
  children,
  initial,
  persist,
}: {
  children: ReactNode;
  /** Server-known preference (`me.language`), adopted on first load if nothing is stored yet. */
  initial?: string | null;
  /** Called with the new language when the user actively changes it (e.g. to persist server-side). */
  persist?: (lang: Locale) => void;
}) {
  const { i18n } = useTranslation();
  const lang: Locale = isLocale(i18n.language) ? i18n.language : defaultLocale;

  const setLang = useCallback(
    (next: Locale) => {
      void i18n.changeLanguage(next);
      if (typeof window !== "undefined") window.localStorage.setItem(LANG_STORAGE_KEY, next);
      document.documentElement.lang = next;
      persist?.(next);
    },
    [i18n, persist],
  );

  // Adopt the server-known preference once, but only when the visitor hasn't already chosen
  // a language on this device. We deliberately do NOT write localStorage or call `persist`
  // here — server stays the source of truth for authenticated users; localStorage is the
  // anonymous fallback.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(LANG_STORAGE_KEY) : null;
    if (!isLocale(stored) && isLocale(initial) && initial !== lang) {
      void i18n.changeLanguage(initial);
    }
    document.documentElement.lang = isLocale(stored) ? stored : isLocale(initial) ? initial : lang;
    // Runs once on mount; `initial` is the server preference known at that point.
  }, [i18n, initial, lang]);

  const value = useMemo<LanguageValue>(() => ({ lang, setLang }), [lang, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Read/set the active UI language. Throws if used outside <LanguageProvider>. */
export function useLanguage(): LanguageValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within <LanguageProvider>");
  return ctx;
}
